import React from 'react'
import styled, { css } from 'styled-components'
import { homeImages } from 'js/imageSources'
import Glp1Button from './Glp1Button'
import { trackGlp1CtaClick } from 'js/analytics'

interface Glp1HeroProps {
  pretitle?: string
  title?: string
  description?: string
  buttonLabel?: string
  url?: string
  minimal?: boolean
  backgroundImage?: string
  focalPoint?: { x: number; y: number }
  backLink?: React.ReactNode
  align?: 'left' | 'center'
  showCta?: boolean
  variant?: 'standard' | 'chungus'
  isArticlePage?: boolean
  onButtonClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

const HeroHeader = styled.header<{
  $backgroundImage: string
  $variant: 'standard' | 'chungus'
  $focalPoint?: { x: number; y: number } | null
}>`
  position: relative;
  width: 100vw;
  min-height: 90vh;
  background-size: cover;
  background-position: ${(props) => {
    if (
      props.$focalPoint &&
      typeof props.$focalPoint.x === 'number' &&
      typeof props.$focalPoint.y === 'number'
    ) {
      const x = props.$focalPoint.x * 100
      const y = props.$focalPoint.y * 100
      return `${x}% ${y}%`
    }
    return 'center 20%'
  }};
  background-image: url(${(props) => props.$backgroundImage});
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: #fff;
  margin-bottom: 0;

  @media (min-width: 768px) {
    max-height: 600px;
    background-position: ${(props) => {
      if (
        props.$focalPoint &&
        typeof props.$focalPoint.x === 'number' &&
        typeof props.$focalPoint.y === 'number'
      ) {
        const x = props.$focalPoint.x * 100
        const y = props.$focalPoint.y * 100
        return `${x}% ${y}%`
      }
      return 'center 20%'
    }};
  }
`

const Overlay = styled.div`
  width: 100%;
  height: 50%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.7) 100%
  );
  position: absolute;
  bottom: 0;
  left: 0;
  @media (min-width: 768px) {
    height: 40%;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.7) 100%
    );
  }
`

const HeroContent = styled.div<{
  $align: 'left' | 'center'
  $variant: 'standard' | 'chungus'
  $isArticlePage?: boolean
}>`
  position: relative;
  z-index: 0;
  max-width: var(--content-width-lg);
  margin: 0 auto;
  padding: var(--spacing-2xl) 0;
  height: 100%;
  justify-content: flex-end;
  width: ${(props) => (props.$isArticlePage ? '90%' : '360px')};
  display: flex;
  flex-direction: column;
  @media (min-width: 768px) {
    width: 100%;
    padding: var(--spacing-2xl) var(--spacing-xl);
    align-items: 'left';
  }
`

const Pretitle = styled.div<{ $align: 'left' | 'center' }>`
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  margin: 0 auto;
  line-height: var(--line-height-body);
  margin-bottom: var(--spacing-xs);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.9;
  @media (min-width: 768px) {
    margin: 0 0 var(--spacing-xs) 0;
    text-align: ${(props) => props.$align};
  }
`

const articlePageTitleStyles = css`
  max-width: 100%;
  font-size: 36px;
  margin-bottom: 0;
  font-weight: 700;
`

const Title = styled.h1<{
  $align: 'left' | 'center'
  $isArticlePage?: boolean
}>`
  color: #fff;
  font-size: var(--font-size-6xl);
  font-weight: 400;
  margin: 0 auto;
  line-height: var(--line-height-heading);
  margin-bottom: var(--spacing-md);
  text-align: center;
  text-wrap: balance;
  max-width: 560px;

  ${(props) => props.$isArticlePage && articlePageTitleStyles}

  @media (min-width: 768px) {
    margin: ${(props) =>
      props.$isArticlePage ? 0 : '0 0 var(--spacing-md) 0'};
    font-size: ${(props) =>
      props.$isArticlePage ? '64px' : 'var(--font-size-7xl)'};
    text-align: ${(props) => props.$align};
  }
`

const TitleLine2 = styled.span`
  font-weight: 700;
`

const Description = styled.h2<{
  $align: 'left' | 'center'
  $isArticlePage?: boolean
}>`
  color: #fff;
  font-size: var(--font-size-lg);
  font-weight: 400;
  line-height: var(--line-height-heading);
  text-align: center;
  text-wrap: balance;
  text-wrap: wrap;
  letter-spacing: -0.28px;
  max-width: 280px;
  margin: 0 auto var(--spacing-lg);

  @media (min-width: 768px) {
    margin: 0;
    max-width: 470px;
    font-size: var(--font-size-2xl);
    text-align: ${(props) => props.$align};
    margin-bottom: var(--spacing-3xl);
    margin-bottom: ${(props) =>
      props.$isArticlePage ? 0 : 'var(--spacing-sm)'};
  }
`

const CtaWrapper = styled.div<{
  $align: 'left' | 'center'
  $variant: 'standard' | 'chungus'
}>`
  margin-top: var(--spacing-sm);
  align-self: center;
`

const Glp1Hero = React.forwardRef<HTMLElement, Glp1HeroProps>(
  (
    {
      pretitle,
      title,
      backgroundImage,
      focalPoint,
      description,
      backLink,
      align = 'center',
      showCta = false,
      buttonLabel,
      url,
      variant = 'standard',
      isArticlePage = false,
      onButtonClick,
    },
    ref,
  ) => {
    const titleSplit = title?.split(/(,)/)
    const titleSimple =
      titleSplit?.[0] && titleSplit?.[1]
        ? titleSplit[0] + titleSplit[1]
        : titleSplit?.[0]

    const titleBold = titleSplit?.[2]

    return (
      <HeroHeader
        ref={ref}
        $backgroundImage={backgroundImage || homeImages.heroImage.src}
        $variant={variant}
        $focalPoint={focalPoint || undefined}
      >
        <Overlay />
        <HeroContent
          $align={align}
          $variant={variant}
          $isArticlePage={isArticlePage}
        >
          {backLink}

          {pretitle && <Pretitle $align={align}>{pretitle}</Pretitle>}

          <Title $align={align} $isArticlePage={isArticlePage}>
            {titleSimple}
            <br />
            <TitleLine2>{titleBold}</TitleLine2>
          </Title>
          <Description $align={align}>{description}</Description>
          {showCta && buttonLabel && url && (
            <CtaWrapper $align={align} $variant={variant}>
              <Glp1Button
                href={url}
                onClick={
                  onButtonClick
                    ? onButtonClick
                    : () => {
                        trackGlp1CtaClick({ source: 'hero' })
                      }
                }
              >
                {buttonLabel}
              </Glp1Button>
            </CtaWrapper>
          )}
        </HeroContent>
      </HeroHeader>
    )
  },
)

Glp1Hero.displayName = 'Glp1Hero'

export default Glp1Hero
